import { getClientCode } from "@entrilia/shared/api/clientApiContext";
import { trackEvent } from "@entrilia/shared/logging";
import adminApi from "../../../../api/adminApi";
import type { PreviewInvoiceCardAutoFill } from "../../../../api/types/expenseManagementTypes";
import useClientContext from "../../../../context/useClientContext";
import type { AutoFillOverride, InvoiceCardPreviewConfig } from "./invoiceCardPreviewState";

const emitVatAndDimensionsUserChanged = (
  invoiceId: string,
  { fieldId, source }: PreviewInvoiceCardAutoFill,
  { from, to }: AutoFillOverride
) => {
  trackEvent("AdminPortal-VatAndDimensions-UserChanged", {
    invoiceId,
    fieldId,
    source: source ?? "",
    fromLabel: from,
    toLabel: to,
    clientCode: getClientCode(),
  });
};

const vendorDefaultLineTooltip = "Filled in from the vendor's default invoice lines.";

const vendorDefaultLineFieldIds = [
  "GlAccount",
  "MultiBookGlAccount",
  "Deal",
  ...[1, 2, 3, 4, 5, 6, 7, 8].map((slot) => `GlobalDimension${slot}`),
];

const vatAndDimensionsCardPreview: InvoiceCardPreviewConfig = {
  getPreview: adminApi.previewInvoiceCard,
  fieldIds: ["Vendor", "VatBusinessPostingGroup", "BillTo", "Currency", "InvoiceDate", "DueDate", "Amount"],
  defaultsTriggerFieldIds: ["Vendor", "VatBusinessPostingGroup", "BillTo"],
  relatedObjectType: "InvoiceLine",
  relatedFieldIds: ["Index", "Amount", "VatPostingSetup", ...vendorDefaultLineFieldIds],
  relatedDefaultsTriggerFieldIds: ["GlAccount", "MultiBookGlAccount"],
  autoFillTooltipsBySource: {
    Vendor: "Filled in from the vendor.",
    GlAccount: "Filled in from the G/L account.",
    PostedHistory: "Filled in from the last posted invoice.",
    PostedHistorySameBillTo: "Filled in from the last posted invoice to this Bill To.",
    PostedHistoryOtherBillTo: "Filled in from the last posted invoice to another Bill To.",
    DocumentRate: "Filled in from the VAT % on the document.",
    VendorDefaultLine: vendorDefaultLineTooltip,
  },
  onAutoFillOverridden: emitVatAndDimensionsUserChanged,
  relatedColumnLayouts: {
    Index: { width: 72, minWidth: 72, flex: 0 },
    Description: { minWidth: 200 },
    GlAccount: { minWidth: 200 },
    MultiBookGlAccount: { minWidth: 200 },
    "VatPostingSetup.VatPercent": { width: 80, flex: 0, align: "right", headerAlign: "right" },
    Amount: { minWidth: 120 },
  },
  getRelatedNoOptionsTexts: ({ VatBusinessPostingGroup }) => ({
    VatPostingSetup: VatBusinessPostingGroup
      ? "No VAT groups for this VAT Bus. Posting Group. Add one in Fund Operations; it may take some time to show up here."
      : "No VAT groups. Choose the VAT Bus. Posting Group first.",
  }),
  summaryDifference: { label: "Difference from Invoice Amount", amountFieldId: "Amount", lineAmountFieldId: "Amount" },
};

const vendorDefaultLinesCardPreview: InvoiceCardPreviewConfig = {
  getPreview: adminApi.previewInvoiceCard,
  fieldIds: ["Vendor", "BillTo"],
  defaultsTriggerFieldIds: ["Vendor", "BillTo"],
  relatedObjectType: "InvoiceLine",
  relatedFieldIds: ["Index", ...vendorDefaultLineFieldIds],
  relatedDefaultsTriggerFieldIds: [],
  autoFillTooltipsBySource: { VendorDefaultLine: vendorDefaultLineTooltip },
};

const useInvoiceCardPreviewConfig = (): InvoiceCardPreviewConfig | undefined => {
  const { isPortalFeatureEnabled, hasAnyPermission } = useClientContext();

  if (!hasAnyPermission(["ViewExpenses", "ManageExpenses"])) {
    return undefined;
  }

  return isPortalFeatureEnabled("ExpenseManagementVatAndDimensionsEnabled")
    ? vatAndDimensionsCardPreview
    : vendorDefaultLinesCardPreview;
};

export default useInvoiceCardPreviewConfig;
